<template>
  <ShortcutWrapper :nShortcutRequiredClaim="UserClaims.GlobalAdmin" @n-shortcut="mutateProduct(null, 'create')">
    <v-container fluid id="product-settings">
      <Dialog :stateId="dialogId" @dialog-closing="close" @close="close" :maxWidth="maxDialogWidth">
        <ProductDetail
          v-if="details"
          :product="focusedProduct"
          @close="close"
          @edit="editClicked"/>
        <ConfirmDelete
          @close="close"
          v-else-if="deleting"
          :title="$t('product')"
          @delete="deleteItem"
          @cancel-delete="close"/>
        <ProductForm
          @close="close"
          @product-mutated="productMutated"
          v-else-if="editing || creating"
          :isEditing="editing"
          :propProduct="focusedProduct"/>
      </Dialog>
      <DataTable
      :items="allProducts"
      :headers="headersProducts"
      :customCells="customTableCells"
      :actions="tableActions"
      :loading.sync="loading"
      @new-product="mutateProduct(null, 'create')"
      @refresh="refreshProducts"
      @click:row="onRowClicked"
      >
        <template #timber-type="{item}">
          <span>{{timberTypeFromInt(item.timberType)}}</span>
        </template>
        <template #wood-type="{item}">
          <span>{{woodTypeFromInt(item.woodType)}}</span>
        </template>
        <template #collect-pieces="{item}">
          <v-icon v-if="item.collectPieceInfo" color="success"
            >mdi-checkbox-marked-circle</v-icon
          >
        </template>
        <template #is-scalable="{item}">
          <v-icon v-if="item.isScalable" color="secondary"
            >mdi-ruler-square</v-icon
          >
        </template>
        <template #related="{item}">
          <Icon
          icon="mdi-information-outline"
          :tooltipText="tooltipText(item)"
          @icon-clicked="onRelatedClicked(item)"/>
        </template>
        <template #actions="{item}">
          <div @click.stop.prevent> <!-- '@click.stop.prevent' prevents the row click listener (which opens details) from interfering with editing and deleting -->
            <v-icon
            small
            class="mr-2"
            color="success"
            data-testid="product-edit-button"
            @click="mutateProduct(item, 'edit')"
            @click.stop.prevent
            :disabled="!userAssignedClaim(UserClaims.GlobalAdmin)"
            >mdi-pencil</v-icon
          >
          <v-icon
            small
            color="error"
            data-testid="product-delete-button"
            @click="mutateProduct(item, 'delete')"
            @click.stop.prevent
            :disabled="!userAssignedClaim(UserClaims.GlobalAdmin)"
            >mdi-delete-forever</v-icon
          >
          </div>
        </template>
      </DataTable>
    </v-container>
  </ShortcutWrapper>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { WoodType, TimberType, UserClaims } from '@/utils/Enumerations.js'
import ProductHeaders from '@/headers/Product'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'ProductSettings',

  components: {
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    ProductForm: () => import('./ProductForm.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ProductDetail: () => import('./ProductDetail.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  created () {
    this.refreshProducts()
  },

  computed: {
    ...mapGetters('product', ['allProducts']),

    headersProducts () {
      return ProductHeaders.productCRUDHeaders()
    },

    tableActions () {
      return [
        {
          icon: 'mdi-refresh',
          text: this.$t('refresh'),
          actionName: 'refresh'
        },
        {
          icon: 'mdi-plus',
          text: this.$t('newProduct'),
          actionName: 'new-product',
          disabled: !userAssignedClaim(UserClaims.GlobalAdmin)
        }
      ]
    },

    customTableCells () {
      return [
        {
          slotName: 'collect-pieces',
          value: 'collectPieceInfo'
        },
        {
          slotName: 'is-scalable',
          value: 'isScalable'
        },
        {
          slotName: 'wood-type',
          value: 'woodType'
        },
        {
          slotName: 'timber-type',
          value: 'timberType'
        },
        {
          slotName: 'related',
          value: 'related'
        },
        {
          slotName: 'actions',
          value: 'actions'
        }
      ]
    },

    maxDialogWidth () {
      const SMALL_DIALOG = '400'
      const BIG_DIALOG = '900'
      const fp = this.focusedProduct
      const viewingDetailsNoSpeciesGradeOrEndUse = this.details && fp.productSpecies?.length + fp.productGrades?.length + fp.productEndUseSorts?.length === 0
      if (viewingDetailsNoSpeciesGradeOrEndUse || this.deleting) return SMALL_DIALOG
      return BIG_DIALOG
    }
  },

  data: () => ({
    editing: false,
    dialog: false,
    deleting: false,
    creating: false,
    loading: false,
    focusedProduct: {},
    currentTab: 0,
    UserClaims,
    details: false,
    dialogId: uniqueDialogId('product-settings-dialog')
  }),

  watch: {
    deleting (val) {
      if (!val) {
        this.refreshProducts()
      }
    },

    dialog (val) {
      if (!val) {
        this.close()
      }
    }
  },

  methods: {
    ...mapActions('product', ['fetchProducts', 'deleteProduct']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    woodTypeFromInt: (x) => WoodType.fromInt(x),
    timberTypeFromInt: (x) => TimberType.fromInt(x),
    userAssignedClaim,
    refreshProducts () {
      this.loading = true
      const requestObj = {
        contractId: undefined,
        includeProduction: true,
        includeScalingInfo: true
      }
      this.fetchProducts(requestObj)
        .finally(() => {
          this.loading = false
        })
    },

    mutateProduct (product, mutationType) {
      this.focusedProduct = Object.assign({}, product)
      this.openOrUpdateDialog(this.dialogId)

      switch (mutationType) {
        case 'delete':
          this.deleting = true
          break
        case 'edit':
          this.editing = true
          this.dialog = true
          break
        case 'create':
          this.focusedProduct = {}
          this.creating = true
          this.dialog = true
          break
        default:
          this.close()
          break
      }
    },

    async deleteItem () {
      await this.deleteProduct(this.focusedProduct.productId)
      this.productMutated()
    },

    productMutated () {
      this.refreshProducts()
      this.close()
    },

    close () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.deleting = false
      this.editing = false
      this.creating = false
      this.dialog = false
      this.details = false
      this.focusedProduct = {}
    },

    onRelatedClicked (item) {
      this.focusedProduct = item
      this.details = true
      this.openOrUpdateDialog(this.dialogId)
    },

    tooltipText (item) {
      let string = ''
      if (item.productSpecies?.length > 0) string = string.concat(`${this.$t('species')}: ${item.productSpecies.map(s => s.commonName).join(', ')}\n`)
      if (item.productGrades?.length > 0) string = string.concat(`${this.$t('grades')}: ${item.productGrades.map(s => s.name).join(', ')}\n`)
      if (item.productEndUseSorts?.length > 0) string = string.concat(`${this.$t('endUseSorts')}: ${item.productEndUseSorts.map(s => s.name).join(', ')}\n`)
      return string
    },

    editClicked (product) {
      this.details = false
      this.mutateProduct(product, 'edit')
    },

    onRowClicked ({ data }) {
      this.onRelatedClicked(data)
    }
  }
}
</script>
